// UI strings for the data tables shown across ShareGate Protect, including the Delegated Reviews app.
// Readers are IT admins and reviewers browsing lists of Microsoft 365 items (users, files, workspaces).
// Translate only the string values. Keep every key and every {{placeholder}} exactly as written.
// "ShareGate" and "Microsoft" are product names and stay untranslated. Use a short, neutral tone.
export const en = {
    // Data table: the empty state shown instead of rows, and the pager below the rows.
    ODataTable: {
        emptyHeading: "We searched everywhere and didn’t find anything",
        emptyDescription: "When we find what you’re looking for, we’ll put it here for you.",
        // Screen-reader-only label of the pager. {{entityLabel}} is the name of the listed items, e.g. "Files".
        paginationLabel: "{{entityLabel}} pagination"
    },
    // Header row of a data table.
    ODataTableHeader: {
        // Screen-reader-only label of the checkbox that selects every row.
        selectAll: "Select all items",
        // Screen-reader-only title of the narrow column holding each row's expand button.
        expandColumn: "Expand"
    },
    // Placeholder table shown while the rows load.
    ODataTableLoading: {
        // Screen-reader-only label of the disabled select-all checkbox.
        selectAll: "Select all items (loading)",
        // Screen-reader-only label of a disabled row checkbox.
        selectItem: "Select item"
    },
    // Button at the start of a row that shows or hides the row's details.
    ExpandButton: {
        // Screen-reader-only label.
        expand: "Expand",
        // Screen-reader-only label.
        collapse: "Collapse"
    },
    // A column title in the table header.
    ODataHeader: {
        // Screen-reader-only label of the handle used to drag the column elsewhere.
        dragColumn: "Drag to reorder column",
        // Screen-reader-only label of the info icon next to the column title.
        columnInformation: "Show column information"
    },
    // Last column of the table, holding each row's actions menu.
    ODataActionHeader: {
        label: "Actions"
    },
    // Actions menu button at the end of a row.
    ActionsCell: {
        // Screen-reader-only label of the menu button.
        rowActions: "Row Actions",
        // Label and tooltip of the spinner shown while an action runs on the row.
        operationInProgress: "Operation in progress…"
    },
    // Messages about selecting rows of the table, shown in tooltips and notifications.
    ODataTableSelection: {
        disabledReason: {
            // Tooltip on a checkbox that can't be used yet.
            resolving: "Still checking your selection against this filter…",
            // Tooltip on a row checkbox that can't be used yet.
            differentSearch:
                "This row wasn't part of the search this selection was made under, so it can't be selected yet."
        },
        // Tooltip on the select-all checkbox. {{limit}} is a formatted number of rows, e.g. "10,000".
        tooManyRows:
            "This view has more than {{limit}} rows, so it can't be checked against your selection. Narrow the filter first.",
        // Notification. {{total}} is a formatted number of rows, e.g. "1,250".
        selectionReplaced: "Selection replaced with all {{total}} matching rows.",
        // Button in the notification that restores the previous selection. Keep it very short.
        undo: "Undo",
        // Error notification. {{limit}} is a formatted number of rows, e.g. "10,000".
        limitReached: "Selection has reached the {{limit}}-row limit. Some changes couldn't be applied."
    },
    // Count shown above the table.
    ODataReportSummary: {
        // {{total}} is the formatted number of rows. {{entityName}} is the name of the listed items, already in the
        // right singular or plural form, e.g. "1 file" or "12 results".
        heading: "{{total}} {{entityName}}"
    },
    // Search field above the table.
    ODataSearch: {
        // Screen-reader-only label.
        label: "OData search",
        placeholder: "Search"
    },
    // Value of a yes/no column, also offered as the value of a yes/no filter.
    Boolean: {
        true: "True",
        false: "False"
    },
    // Value of a cell whose content is still being computed.
    PendingCell: {
        label: "Pending…"
    },
    // Shared by every table cell.
    Cells: {
        // Shown in a cell that has no value. Keep it a single short mark.
        empty: "-"
    },
    // Cell listing the people tied to a row (owners, users…), as avatars or a single name.
    UsersCell: {
        // Shown instead of a person's name when it isn't known.
        unknownUser: "Unknown",
        // Screen-reader-only label of a person's avatar, which opens their details. {{name}} is the person's name.
        viewDetails: "View {{name}} details",
        // Empty state of a cell that lists people: a link's owners, creators or sharers, a review's reviewers, and so on.
        owners_zero: "No owners",
        creators_zero: "No creators",
        users_zero: "No users",
        guests_zero: "No guests",
        sharers_zero: "No sharers",
        reviewers_zero: "No reviewers",
        recipients_zero: "No recipients"
    },
    // Conditions of a filter on a column, picked from a menu on the filter and read inside the filter sentence
    // (e.g. "Display name contains report"). Keep them lowercase, since they sit in the middle of that sentence.
    FilterOperators: {
        equals: "equals",
        doesNotEqual: "does not equal",
        contains: "contains",
        doesNotContain: "does not contain",
        startsWith: "starts with",
        endsWith: "ends with",
        isEmpty: "is empty",
        isNotEmpty: "is not empty",
        isEqualTo: "is equal to",
        isGreaterThan: "is greater than",
        isLessThan: "is less than",
        isGreaterThanOrEqualTo: "is greater than or equal to",
        isLessThanOrEqualTo: "is less than or equal to",
        // Followed by a list of values, e.g. "Status in Active, Archived".
        in: "in",
        is: "is",
        isNot: "is not",
        // Starts the condition of a filter matching any picked item, e.g. "Owners is any of 2 selected".
        isAnyOf: "is any of",
        // Starts the condition of a filter matching every picked item, e.g. "Owners is all of 2 selected".
        isAllOf: "is all of"
    },
    // Conditions of a filter on a date column, read inside the filter sentence (e.g. "Created on past 30 days",
    // "Created on before Jan 1, 2025"). Keep them lowercase, since they sit in the middle of that sentence.
    DateFilterOperators: {
        // Within the last N days.
        past: "past",
        olderThan: "older than",
        // More than N days in the future.
        lessThan: "less than",
        // Within the next N days.
        next: "next",
        // Followed by a date.
        before: "before",
        // Followed by a date.
        after: "after",
        beforeNow: "before now",
        afterNow: "after now",
        isEmpty: "is empty",
        isNotEmpty: "is not empty"
    },
    // Picks whether a filter on a list of related items must match any of them or all of them.
    // It starts the filter sentence, e.g. "Any of Sharing links / File name equals report.xlsx".
    FilterQuantifiers: {
        any: "Any of",
        all: "All of"
    },
    // Word combining two filters. Shown in capitals between filters, and in lowercase inside a filter group's summary.
    LogicalOperators: {
        and: "and",
        or: "or"
    },
    // One filter written out as a sentence, in a filter group's summary and in automation rule cards.
    FilterSentence: {
        // {{field}} is the column name, {{operator}} a condition from the groups above, {{value}} the typed or picked value.
        condition: "{{field}} {{operator}} {{value}}",
        // A condition that takes no value, e.g. "Display name is empty".
        conditionWithoutValue: "{{field}} {{operator}}",
        // {{quantifier}} is "Any of" or "All of", from the group above.
        quantifiedCondition: "{{quantifier}} {{field}} {{operator}} {{value}}",
        quantifiedConditionWithoutValue: "{{quantifier}} {{field}} {{operator}}"
    },
    // A group of filters, shown as one tag summarizing the filters it holds.
    FilterGroupTag: {
        // Shown instead of the summary while the group holds no filter.
        placeholder: "Filter group…",
        // Tooltip on the group's summary, which opens the filters it holds.
        openGroup: "Open filter group",
        // Ends a summary cut short to fit, e.g. "Name contains a or 2 more conditions".
        moreConditions_one: "{{count}} more condition",
        moreConditions_other: "{{count}} more conditions"
    },
    // Durations in a date filter, e.g. "30 days" or "1 year, 2 months". {{formattedCount}} is a formatted number.
    Durations: {
        years_one: "{{formattedCount}} year",
        years_other: "{{formattedCount}} years",
        months_one: "{{formattedCount}} month",
        months_other: "{{formattedCount}} months",
        days_one: "{{formattedCount}} day",
        days_other: "{{formattedCount}} days",
        hours_one: "{{formattedCount}} hour",
        hours_other: "{{formattedCount}} hours",
        minutes_one: "{{formattedCount}} minute",
        minutes_other: "{{formattedCount}} minutes",
        seconds_one: "{{formattedCount}} second",
        seconds_other: "{{formattedCount}} seconds"
    },
    // A date condition on a duration counted back from today, read inside the filter sentence (e.g. "Latest activity
    // more than 30 days ago"). {{duration}} is one or more durations from the group above, e.g. "30 days". Keep them
    // lowercase.
    DateAgoConditions: {
        past: "past {{duration}} ago",
        olderThan: "older than {{duration}} ago",
        moreThan: "more than {{duration}} ago",
        lessThan: "less than {{duration}} ago"
    },
    // Button after the filters above the table that removes every filter.
    ODataFilters: {
        clear: "Clear"
    },
    // Button between two filters that switches between AND and OR.
    ODataFilterOperatorToggle: {
        // Screen-reader-only label. {{operator}} is the current word in capitals, e.g. "AND".
        label: "Toggle filter operator, currently {{operator}}"
    },
    // A filter shown as a tag above the table.
    ODataFilterTagWrapper: {
        // Screen-reader-only label of the button that removes the filter.
        remove: "Remove filter"
    },
    // Popover listing the filters of a filter group.
    FilterGroupPopover: {
        // Screen-reader-only label of the button that adds a filter to the group.
        addFilterLabel: "Filter",
        // Visible text of the same button.
        addFilter: "Filters"
    },
    // Shared by the filter tags: messages under a filter's value field, and the value picker's placeholder.
    FilterTags: {
        // {{field}} is the column name, e.g. "Display name".
        required: "{{field}} is required.",
        // {{field}} is the column name, e.g. "Site ID".
        invalid: "Please enter a valid {{field}}.",
        selectPlaceholder: "Select…"
    },
    // Number of days in a date filter, e.g. "Created on past [30] days".
    RelativeDateValue: {
        // Shown after the number field, so it holds no number.
        days_one: "day",
        days_other: "days",
        // Shown after the number field, so it holds no number.
        daysAgo_one: "day ago",
        daysAgo_other: "days ago",
        // Shown after the number field while it is empty.
        emptyDays: "days",
        // Shown after the number field while it is empty.
        emptyDaysAgo: "days ago",
        // Screen-reader-only label of the number field. {{field}} is the column name.
        label: "{{field}} days"
    },
    // Date picker in a date filter.
    AbsoluteDateValue: {
        // Screen-reader-only label. {{field}} is the column name.
        label: "{{field}} date"
    },
    // Row of preset filters above the table, each with its number of rows.
    ODataInlineFilters: {
        // Screen-reader-only label.
        label: "Inline filters"
    },
    // Button above the table that opens the Columns popover, which picks the columns the table shows.
    ODataColumnsPopover: {
        // Screen-reader-only label of the button.
        label: "Display",
        // Visible text of the same button.
        trigger: "Columns",
        // Screen-reader-only label of the search field.
        searchLabel: "Search properties",
        searchPlaceholder: "Search properties",
        noResults: "No properties match your search.",
        // Title of the section listing the columns of the listed items themselves.
        entityProperties: "Display properties",
        // Screen-reader-only label of each section's list of columns.
        propertiesLabel: "Entity properties"
    },
    // Button above the table that opens the menu of columns to filter on.
    ODataFilterMenu: {
        // Screen-reader-only label of the button.
        label: "Filter",
        // Visible text of the same button.
        trigger: "Filters",
        addGroup: "Add filter group…",
        // Screen-reader-only label of the search field at the top of the menu.
        searchLabel: "Search filters",
        searchPlaceholder: "Filter…"
    },
    // Search results of the filter menu.
    FilteredFilterResults: {
        // Shown when the search matches no column.
        noMatches: "No filters match"
    },
    // Section of the filter menu listing related items (e.g. a file's owner) whose columns can be filtered on.
    RelationshipSubmenus: {
        title: "Relationship"
    },
    // Submenu of the filter menu for a column holding a list of values.
    EnumCollectionFilterSubmenu: {
        // Option filtering the column on having at least one value.
        isNotEmpty: "Is not empty"
    },
    // Filters picking users or workspaces by name from a searchable list.
    EntityPicker: {
        user: {
            // Name of the filter, e.g. on its tag. Starts with a capital.
            label: "User",
            // Read inside a sentence, e.g. "Select user…". Lowercase, unless the language capitalizes nouns.
            noun: "user",
            // Read inside a sentence, e.g. "Select users…". Lowercase, unless the language capitalizes nouns.
            pluralNoun: "users"
        },
        workspace: {
            // Name of the filter, e.g. on its tag. Starts with a capital.
            label: "Workspace",
            // Read inside a sentence, e.g. "Select workspace…". Lowercase, unless the language capitalizes nouns.
            noun: "workspace",
            // Read inside a sentence, e.g. "Select workspaces…". Lowercase, unless the language capitalizes nouns.
            pluralNoun: "workspaces"
        },
        // Menu option adding the filter. {{noun}} is the kind of item picked, e.g. "users" or "owners".
        select: "Select {{noun}}…",
        // {{noun}} is the kind of item searched, e.g. "users" or "Owners".
        searchPlaceholder: "Search {{noun}}…",
        // Screen-reader-only label of the search field. {{noun}} is the kind of item searched, e.g. "users".
        searchLabel: "Search {{noun}}",
        // {{noun}} is the kind of item searched, in lowercase, e.g. "users".
        startTyping: "Start typing to search through {{noun}}.",
        searching: "Searching…",
        // {{noun}} is the kind of item searched, in lowercase, e.g. "users".
        noResults: "No {{noun}} found with that name. Check the spelling or try to search for another.",
        // Shown instead of a picked item's name while it loads.
        loading: "Loading…",
        // Shown instead of a picked item's name when it can't be found.
        unknown: "Unknown",
        // Number of picked items, shown on the filter's value.
        selectedCount_one: "{{count}} selected",
        selectedCount_other: "{{count}} selected",
        // Title of the list of picked items.
        selected: "Selected",
        // Title of the list of search results.
        results: "Results"
    }
} as const;
